import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIamPermission, usePermissions } from "@/features/auth/hooks/usePermissions";
import { sendAskNexusMessage, stopAskNexusAnswer } from "@/features/ask-nexus/api/ask-nexus.api";
import { useAskNexusPanel } from "@/features/ask-nexus/store/ask-nexus-panel.store";
import { askNexusAvailabilityQueryOptions, askNexusKeys } from "@/lib/queries/ask-nexus.queries";

export interface AskNexusAccess {
  canView: boolean;
  canSend: boolean;
  canStop: boolean;
}

/**
 * Two separate checks, both needed: the environment runs Ask Nexus, and the
 * caller may use it there. The second is the rule the API applies to every Ask
 * Nexus route: the key granted on the active organization, or SUPER_TENANT
 * membership. UI gating only; the server enforces both again.
 */
export function useAskNexusAccess(): AskNexusAccess {
  const permissions = usePermissions();
  const isGranted = useIamPermission(permissions.ASK_NEXUS_VIEW_PAGE);
  const isSendGranted = useIamPermission(permissions.ASK_NEXUS_SEND_MESSAGE);
  const isStopGranted = useIamPermission(permissions.ASK_NEXUS_STOP_ANSWER);
  const availability = useQuery(askNexusAvailabilityQueryOptions(isGranted));
  const canView = isGranted && availability.data?.isAvailable === true;
  return { canView, canSend: canView && isSendGranted, canStop: canView && isStopGranted };
}

export function useSendAskNexusMessage(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sendAskNexusMessage,
    meta: { skipGlobalError: true },
    onSuccess: (conversationId) => {
      // A reply that lands after an organization switch must not open in the other organization.
      if (useAskNexusPanel.getState().tenantId === tenantId) {
        useAskNexusPanel.getState().openConversation(conversationId);
      }
      void queryClient.invalidateQueries({ queryKey: askNexusKeys.tenant(tenantId) });
    },
  });
}

export function useStopAskNexusAnswer(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: stopAskNexusAnswer,
    meta: { skipGlobalError: true },
    onSettled: (_result, _error, conversationId) => {
      void queryClient.invalidateQueries({ queryKey: askNexusKeys.conversation(tenantId, conversationId) });
    },
  });
}
